import { Matrix, invertMatrix } from "../../shared/boards";

// the view onto a board: where its origin sits on the stage, how big it is, how far it is turned and whether it
// is mirrored. a board point goes through the mirror first, then the turn, then the zoom, then the move, so a
// turned board still zooms around the finger and the mirror never changes which way the turn goes on screen

export interface View {
  x: number;
  y: number;
  scale: number;
  // radians, clockwise on screen
  rot: number;
  // mirrored left to right
  flip: boolean;
}

export const MIN_SCALE = 0.08;
export const MAX_SCALE = 10;
const SNAP = (4 * Math.PI) / 180;

export function freshView(): View {
  return { x: 0, y: 0, scale: 1, rot: 0, flip: false };
}

export function viewMatrix(view: View): Matrix {
  const cos = Math.cos(view.rot) * view.scale;
  const sin = Math.sin(view.rot) * view.scale;
  const side = view.flip ? -1 : 1;
  return [cos * side, sin * side, -sin, cos, view.x, view.y];
}

export function toBoardPoint(view: View, x: number, y: number): [number, number] {
  const m = invertMatrix(viewMatrix(view));
  return [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];
}

export function toScreenPoint(view: View, x: number, y: number): [number, number] {
  const m = viewMatrix(view);
  return [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];
}

// the view that keeps a board point under a screen point while the zoom, turn or mirror changes
export function placeView(look: Pick<View, "scale" | "rot" | "flip">, screen: [number, number], board: [number, number]): View {
  const next: View = { ...look, x: 0, y: 0 };
  const [sx, sy] = toScreenPoint(next, board[0], board[1]);
  return { ...next, x: screen[0] - sx, y: screen[1] - sy };
}

// first b, then a
export function multiply(a: Matrix, b: Matrix): Matrix {
  return [
    a[0] * b[0] + a[2] * b[1],
    a[1] * b[0] + a[3] * b[1],
    a[0] * b[2] + a[2] * b[3],
    a[1] * b[2] + a[3] * b[3],
    a[0] * b[4] + a[2] * b[5] + a[4],
    a[1] * b[4] + a[3] * b[5] + a[5]
  ];
}

export function cssOf(m: Matrix): string {
  return `matrix(${m.map((value) => Math.round(value * 1e6) / 1e6).join(", ")})`;
}

// what moves a picture drawn for one view so it looks right in another
export function between(from: View, to: View): Matrix {
  return multiply(viewMatrix(to), invertMatrix(viewMatrix(from)));
}

// a move made on the board, as the same move on the stage
export function onStage(view: View, m: Matrix): Matrix {
  const v = viewMatrix(view);
  return multiply(multiply(v, m), invertMatrix(v));
}

export function turnBy(view: View, radians: number, around: [number, number]): View {
  return placeView({ scale: view.scale, rot: wrap(view.rot + radians), flip: view.flip }, around, toBoardPoint(view, around[0], around[1]));
}

export function turnTo(view: View, radians: number, around: [number, number]): View {
  return turnBy(view, wrap(radians) - view.rot, around);
}

// mirroring around a point leaves that point where it is
export function flipAround(view: View, around: [number, number]): View {
  return placeView({ scale: view.scale, rot: view.rot, flip: !view.flip }, around, toBoardPoint(view, around[0], around[1]));
}

export function zoomTo(view: View, scale: number, around: [number, number]): View {
  const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
  return placeView({ scale: next, rot: view.rot, flip: view.flip }, around, toBoardPoint(view, around[0], around[1]));
}

// -pi..pi
export function wrap(radians: number): number {
  const turn = Math.PI * 2;
  return ((((radians + Math.PI) % turn) + turn) % turn) - Math.PI;
}

export function degrees(radians: number): number {
  return Math.round((radians * 180) / Math.PI);
}

export function radians(deg: number): number {
  return (deg * Math.PI) / 180;
}

// close to upright or a quarter turn settles onto it, anything else stays put
export function snapped(rot: number): number {
  const quarter = Math.PI / 2;
  const near = Math.round(rot / quarter) * quarter;
  return Math.abs(rot - near) < SNAP ? wrap(near) : rot;
}

// the screen box that holds a board box once it is turned and mirrored
export function screenExtent(view: View, box: { x0: number; y0: number; x1: number; y1: number }): { x0: number; y0: number; x1: number; y1: number } {
  const corners = [
    toScreenPoint(view, box.x0, box.y0),
    toScreenPoint(view, box.x1, box.y0),
    toScreenPoint(view, box.x0, box.y1),
    toScreenPoint(view, box.x1, box.y1)
  ];
  const xs = corners.map((corner) => corner[0]);
  const ys = corners.map((corner) => corner[1]);
  return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
}

// the board box that holds a screen rectangle, for knowing what to paint
export function boardExtent(view: View, width: number, height: number): { x0: number; y0: number; x1: number; y1: number } {
  const corners = [toBoardPoint(view, 0, 0), toBoardPoint(view, width, 0), toBoardPoint(view, 0, height), toBoardPoint(view, width, height)];
  const xs = corners.map((corner) => corner[0]);
  const ys = corners.map((corner) => corner[1]);
  return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
}

export function sameView(a: View, b: View): boolean {
  return a.x === b.x && a.y === b.y && a.scale === b.scale && a.rot === b.rot && a.flip === b.flip;
}

export function readView(raw: unknown): View | null {
  const saved = raw as Partial<View> | null;
  if (!saved || ![saved.x, saved.y, saved.scale].every((value) => Number.isFinite(value)) || (saved.scale as number) <= 0) return null;
  const rot = Number.isFinite(saved.rot) ? wrap(saved.rot as number) : 0;
  return { x: saved.x as number, y: saved.y as number, scale: saved.scale as number, rot, flip: saved.flip === true };
}
